import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { all_countries, Country } from '../data/countries';
import { all_provinces, Province } from "../data/provinces";
import { BreakpointObserver } from '@angular/cdk/layout';
import { StepperOrientation } from '@angular/material/stepper';
import { Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { IndividualFormDataInterface } from "../../../interfaces/individual";
import { Medicine } from "../../../interfaces/medicine";
import { FormService } from "../../services/form.service";
import { DeliveryType } from 'src/interfaces/delivery-type';
import { Router } from '@angular/router';
import { Company, CompanyFormDataInterface, CompanyType } from 'src/interfaces/company';
import { ForWho as ForWhoEnum } from 'src/enums/for-who';
import { MatStepper } from '@angular/material/stepper';
import { LoaderService } from 'src/core/services/loader.service';

@Component({
    selector: 'form-public-form',
    templateUrl: './public-form.component.html',
    styleUrls: ['./public-form.component.scss'],
    standalone: false
})
export class PublicFormComponent implements OnInit {
  countries: Country[] = all_countries;
  provinces: Province[] = all_provinces;
  filteredMedicine: Observable<Medicine[]>;
  filteredCountries: Observable<Country[]>;
  fileKey: string = "";
  medicine: Medicine[] = [];
  deliveryTypes: DeliveryType[] = [];
  companyTypes: CompanyType[] = [];
  acceptFiles: string = ".pdf";
  isEditable: boolean = true;
  orderId: number | null = null;
  @ViewChild('stepper') private _stepper: MatStepper;

  individualPrescriptionDetailsForm = this.fb.group({
    title: ['', Validators.required],
    initials: ['', Validators.required],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    gender: ['', Validators.required],
    dateOfBirth: ['', Validators.required],
    isAnimal: ['', Validators.required],
    phoneNumber: ['', Validators.required],
    phoneNumberAlt: [''],
    email: ['', Validators.compose([Validators.required, Validators.email])],
    emailAlt: ['', Validators.email],
    numberOfDependants: ['', Validators.compose([Validators.min(1)])],
    buildingAptNumber: ['', Validators.required],
    addressLine1: ['', Validators.required],
    addressLine2: [''],
    province: ['', Validators.required],
    postcode: ['', Validators.compose([Validators.required, Validators.pattern(/^[0-9]{4,5}$/)])],
    country: ['', Validators.required],
    deliveryType: ['', Validators.required]
  });

  ForWho = ForWhoEnum;
  forWhoOptions: ForWhoEnum[] = [ForWhoEnum.Me, ForWhoEnum.SomeOneElse, ForWhoEnum.Company];

  customerDetailsForm = this.fb.group({
    title: ['', Validators.required],
    initials: ['', Validators.required],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    gender: ['', Validators.required],
    dateOfBirth: ['', Validators.required],
    phoneNumber: ['', Validators.required],
    alternativeNumber: [''],
    email: ['', Validators.compose([Validators.required, Validators.email])],
    emailAlt: ['', Validators.email],
    forWho: ['', Validators.required]
  });



  selfForm = this.fb.group({
    numberOfDependants: ['', Validators.min(1)],
    buildingAptNumber: ['', Validators.required],
    addressLine1: ['', Validators.required],
    addressLine2: [''],
    province: ['', Validators.required],
    postcode: ['', Validators.required],
    country: ['', Validators.required],
    deliveryType: ['', Validators.required]
  });


  companyPrescriptionDetailsForm = this.fb.group({
    companyName: ['', Validators.required],
    email: ['', Validators.compose([Validators.required, Validators.email])],
    emailAlt: ['', Validators.compose([Validators.required, Validators.email])],
    phoneNumber: ['', Validators.required],
    phoneNumberAlt: [''],
    practiceNumber: [''],
    vatRegistrationNumber: [''],
    companyType: ['', Validators.required],
    buildingAptNumber: ['', Validators.required],
    addressLine1: ['', Validators.required],
    addressLine2: ['', Validators.required],
    province: ['', Validators.required],
    postcode: ['', Validators.required],
    country: ['', Validators.required]
  });

  uploadPrescriptionForm = this.fb.group({
    fileKey: ['', Validators.required]
  });

  searchControl = this.fb.control(['']);

  stepperOrientation: Observable<StepperOrientation>;
  isSmallScreen: Observable<boolean>;
  isLoading: boolean = false
  filteredCountriesForCompany: Observable<Country[]>;
  filteredCountriesForIndividual: Observable<Country[]>;

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private formService: FormService,
    private breakpointObserver: BreakpointObserver,
    private loaderService: LoaderService
  ) {

    this.stepperOrientation = this.breakpointObserver
      .observe('(min-width: 900px)')
      .pipe(map(({ matches }) => (matches ? 'horizontal' : 'vertical')));

    this.isSmallScreen = breakpointObserver
      .observe('(min-width: 900px)')
      .pipe(map(({ matches }) => (matches ? true : false)));
  }

  ngOnInit(): void {

    this
      .loaderService
      .isLoading
      .subscribe(isLoading => {
        this.isLoading = isLoading;
      });

    this
      .formService
      .getFormData(['company_type', 'delivery_type'])
      .subscribe(data => {
        this.deliveryTypes = data.delivery_type;
        this.companyTypes = data.company_type;
      });

    this.filteredCountries = this
      .selfForm
      .controls['country']
      .valueChanges
      .pipe(
        startWith('South Africa'),
        map(value => this._filterCountries(value))
      );

    this.filteredCountriesForIndividual = this
      .individualPrescriptionDetailsForm
      .controls['country']
      .valueChanges
      .pipe(
        startWith('South Africa'),
        map(value => this._filterCountries(value))
      );

    this.filteredCountriesForCompany = this
      .companyPrescriptionDetailsForm
      .controls['country']
      .valueChanges
      .pipe(
        startWith('South Africa'),
        map(value => this._filterCountries(value))
      );

  }

  _filterCountries(value: string) {
    return this
      .countries
      .filter((country) => {
        return country
          .name
          .toLowerCase()
          .includes(
            value.toLowerCase()
          )
      })
  }

  onFileUploaded(success: boolean) {
    if (success)
      this.router.navigate(['/thanks']);
  }

  submitPrescription() {
    this.save();
  }

  save() {
    // let individualData: IndividualFormDataInterface | undefined;
    // let companyData: CompanyFormDataInterface | undefined;
    // let company: Company | undefined;
    // const PATIENT_ID: number = 1;

    // switch (this.customerDetailsForm.value.forWho) {
    //   case ForWhoEnum.Me:
    //     individualData = {
    //       first_name: this.customerDetailsForm.value.firstName,
    //       last_name: this.customerDetailsForm.value.lastName,
    //       initials: this.customerDetailsForm.value.initials,
    //       gender: this.customerDetailsForm.value.gender,
    //       title: this.customerDetailsForm.value.title,
    //       dependant_number: parseInt(this.selfForm.value.numberOfDependants),
    //       date_of_birth: this.customerDetailsForm.value.dateOfBirth,
    //       is_animal: false,
    //       phone_number: this.customerDetailsForm.value.phoneNumber,
    //       phone_number_alt: this.customerDetailsForm.value.alternativeNumber,
    //       email: this.customerDetailsForm.value.email,
    //       email_alt: this.customerDetailsForm.value.emailAlt,
    //       building_apt_number: parseInt(this.selfForm.value.buildingAptNumber),
    //       address_1: this.selfForm.value.addressLine1,
    //       address_2: this.selfForm.value.addressLine2,
    //       province: this.selfForm.value.province,
    //       postcode: this.selfForm.value.postcode,
    //       country: this.selfForm.value.country,
    //       company_id: undefined,
    //       individual_type_id: PATIENT_ID,
    //       delivery_type_id: parseInt(this.selfForm.value.deliveryType),
    //       medicine: undefined
    //     };

    //     break;

    //   case ForWhoEnum.SomeOneElse:
    //     const isAnimal: boolean = Boolean(parseInt(
    //       this.individualPrescriptionDetailsForm.value.isAnimal,
    //     ));

    //     individualData = {
    //       first_name: this.individualPrescriptionDetailsForm.value.firstName,
    //       last_name: this.individualPrescriptionDetailsForm.value.lastName,
    //       initials: this.individualPrescriptionDetailsForm.value.initials,
    //       gender: this.individualPrescriptionDetailsForm.value.gender,
    //       title: this.individualPrescriptionDetailsForm.value.title,
    //       dependant_number: parseInt(this.individualPrescriptionDetailsForm.value.numberOfDependants),
    //       date_of_birth: this.individualPrescriptionDetailsForm.value.dateOfBirth,
    //       is_animal: isAnimal,
    //       phone_number: this.individualPrescriptionDetailsForm.value.phoneNumber,
    //       phone_number_alt: this.individualPrescriptionDetailsForm.value.alternativeNumber,
    //       email: this.individualPrescriptionDetailsForm.value.email,
    //       email_alt: this.individualPrescriptionDetailsForm.value.emailAlt,
    //       building_apt_number: parseInt(this.individualPrescriptionDetailsForm.value.buildingAptNumber),
    //       address_1: this.individualPrescriptionDetailsForm.value.addressLine1,
    //       address_2: this.individualPrescriptionDetailsForm.value.addressLine2,
    //       province: this.individualPrescriptionDetailsForm.value.province,
    //       postcode: this.individualPrescriptionDetailsForm.value.postcode,
    //       country: this.individualPrescriptionDetailsForm.value.country,
    //       company_id: undefined,
    //       individual_type_id: PATIENT_ID,
    //       delivery_type_id: parseInt(this.individualPrescriptionDetailsForm.value.deliveryType),
    //       medicine: undefined
    //     };

    //     break;

    //   case ForWhoEnum.Company:

    //     company = <Company> {
    //       id: undefined,
    //       name: this.companyPrescriptionDetailsForm.value.companyName,
    //       email: this.companyPrescriptionDetailsForm.value.email,
    //       email_alt: this.companyPrescriptionDetailsForm.value.emailAlt,
    //       phone_number: this.companyPrescriptionDetailsForm.value.phoneNumber,
    //       phone_number_alt: this.companyPrescriptionDetailsForm.value.phoneNumberAlt,
    //       practice_number: this.companyPrescriptionDetailsForm.value.practiceNumber,
    //       vat_registration_number: this.companyPrescriptionDetailsForm.value.vatRegistrationNumber,
    //       company_type_id: this.companyPrescriptionDetailsForm.value.companyType,
    //       created_at: undefined,
    //       updated_at: undefined,
    //       deleted_at: undefined,
    //       address_id: undefined,
    //       primary_contact_id: undefined,
    //       addresses: []
    //     }

    //     companyData = {
    //       first_name: this.customerDetailsForm.value.firstName,
    //       last_name: this.customerDetailsForm.value.lastName,
    //       initials: this.customerDetailsForm.value.initials,
    //       gender: this.customerDetailsForm.value.gender,
    //       title: this.customerDetailsForm.value.title,
    //       dependant_number: parseInt(this.selfForm.value.numberOfDependants),
    //       date_of_birth: this.customerDetailsForm.value.dateOfBirth,
    //       phone_number: this.customerDetailsForm.value.phoneNumber,
    //       phone_number_alt: this.customerDetailsForm.value.alternativeNumber,
    //       email: this.customerDetailsForm.value.email,
    //       email_alt: this.customerDetailsForm.value.emailAlt,
    //       building_apt_number: parseInt(this.selfForm.value.buildingAptNumber),
    //       address_1: this.selfForm.value.addressLine1,
    //       address_2: this.selfForm.value.addressLine2,
    //       province: this.selfForm.value.province,
    //       postcode: this.selfForm.value.postcode,
    //       country: this.selfForm.value.country,
    //       company_id: undefined,
    //       individual_type_id: PATIENT_ID,
    //       delivery_type_id: parseInt(this.selfForm.value.deliveryType),
    //       company: company,
    //       medicine: undefined
    //     }
    // }

    // if (!individualData && !companyData) return;

    // const prescriptionData: any = individualData ? individualData : companyData;

    // (async () => {
    //   const response = await this.formService.submitPrescription(prescriptionData);
    //   if (!response) return;
    //   if (!response.success) return;
    //   this.orderId = response.data.id;
    //   this.isEditable = false;
    //   this._stepper.next();
    // })();

  }
}
